import { PLATFORM_ENUM } from 'services/api/constants'
import * as yup from 'yup'
import i18n from 'config/i18n'
import { REQUIREMENT_TYPE_ENUM } from 'state/campaigns'
import { BLOCKED_CAMPAIGNS_TYPE } from '../CampaignRequirements'
import { ERROR_MESSAGES } from './errorMessages'
import { COMPLETE_SET_UP_ERRORS, ERROR_CODES } from 'state/error'

export const locationsRequirementValidation = (locations: Record<string, string | null>[] | null) => {
    let isValid = true
    if (!locations || !Array.isArray(locations)) {
        isValid = false
    }
    if (Array.isArray(locations)) {
        locations.forEach((region: any) => {
            //SG only region and required
            if (region.hasOwnProperty('region') && region['region'] === null) {
                isValid = false
            }
            //ID province: required , city: optional
            if (
                region.hasOwnProperty('city') &&
                region['city'] === null &&
                region.hasOwnProperty('province') &&
                region['province'] === null
            ) {
                isValid = false
            }
            //TW city: required , district: optional
            if (
                region.hasOwnProperty('district') &&
                region['district'] === null &&
                region.hasOwnProperty('city') &&
                region['city'] === null
            ) {
                isValid = false
            }
            //HK only district and required
            if (region.hasOwnProperty('district') && region['district'] === null) {
                isValid = false
            }
            //MY state: required , district: optional
            if (
                region.hasOwnProperty('state') &&
                region['state'] === null &&
                region.hasOwnProperty('district') &&
                region['district'] === null
            ) {
                isValid = false
            }
            //PH region: required , city: optional
            if (
                region.hasOwnProperty('city') &&
                region['city'] === null &&
                region.hasOwnProperty('region') &&
                region['region'] === null
            ) {
                isValid = false
            } else {
                isValid = true
            }
            //TH region: required , city: optional
            if (
                region.hasOwnProperty('province') &&
                region['province'] === null &&
                region.hasOwnProperty('district') &&
                region['district'] === null
            ) {
                isValid = false
            }
            //VN region: required , city: optional
            if (
                region.hasOwnProperty('province/city') &&
                region['province/city'] === null &&
                region.hasOwnProperty('city/district/town') &&
                region['city/district/town'] === null
            ) {
                isValid = false
            }
        })
    }
    return isValid
}

const locationSchema = yup
    .object({
        id: yup.number().nullable(),
        name: yup.string().nullable(),
        uuid: yup.string().required(),
    })
    .nullable()

const getLocationSchema = (key: string) =>
    yup.object({
        [key]: locationSchema,
    })

export const baseValidationSchema = (isCampaignDraftOrForEdit: boolean): any => {
    return yup.object({
        images: yup
            .array()
            .of(
                yup.object({
                    image: yup.string(),
                    img_main: yup.string(),
                    uuid: yup.string(),
                }),
            )

            .nullable()
            .test({
                message: ERROR_MESSAGES[COMPLETE_SET_UP_ERRORS.IMAGES][ERROR_CODES.INPUT_REQUIRED],
                test: (value: any[] | null) => {
                    if (isCampaignDraftOrForEdit) {
                        return true
                    } else {
                        if (Array.isArray(value)) {
                            return Boolean(value.length)
                        } else {
                            return Boolean(value)
                        }
                    }
                },
            }),

        description: yup
            .string()
            .nullable()
            .test({
                message: ERROR_MESSAGES[COMPLETE_SET_UP_ERRORS.DESCRIPTION][ERROR_CODES.INPUT_REQUIRED],
                test: (value: string | null) => {
                    if (isCampaignDraftOrForEdit) {
                        return true
                    } else {
                        return Boolean(value)
                    }
                },
            }),
        requirementsData: yup.array().of(
            yup.object().shape({
                type: yup.mixed(),
                properties: yup
                    .object()
                    .when('type', {
                        is: REQUIREMENT_TYPE_ENUM.AGE,
                        then: yup.object({
                            min: yup
                                .number()
                                .nullable()
                                .integer()
                                .min(16, i18n.t('errorMessage.businessLogic.minAgeSixteen'))
                                .test({
                                    message:
                                        ERROR_MESSAGES[COMPLETE_SET_UP_ERRORS.REQUIREMENTS][REQUIREMENT_TYPE_ENUM.AGE][
                                            'min'
                                        ][ERROR_CODES.INPUT_REQUIRED],
                                    test: function (this, value: number | null) {
                                        if (isCampaignDraftOrForEdit) {
                                            return true
                                        } else {
                                            if (Boolean(this.parent.max)) {
                                                return true
                                            } else {
                                                return Boolean(value)
                                            }
                                        }
                                    },
                                }),
                            max: yup
                                .number()
                                .nullable()
                                .integer()
                                .min(16, i18n.t('errorMessage.businessLogic.minAgeSixteen'))
                                .test({
                                    message:
                                        ERROR_MESSAGES[COMPLETE_SET_UP_ERRORS.REQUIREMENTS][REQUIREMENT_TYPE_ENUM.AGE][
                                            'max'
                                        ][ERROR_CODES.INPUT_REQUIRED],
                                    test: function (this, value: number | null) {
                                        if (isCampaignDraftOrForEdit) {
                                            return true
                                        } else {
                                            if (Boolean(this.parent.min)) {
                                                return true
                                            } else {
                                                return Boolean(value)
                                            }
                                        }
                                    },
                                })
                                .test({
                                    message: () => i18n.t('errorMessage.validation.maxAgeLessThanMinAge'),
                                    test: function (value: number | null) {
                                        const minValue = this.parent.min
                                        return minValue && value ? value > minValue : true
                                    },
                                }),
                        }),
                    })
                    .when('type', {
                        is: REQUIREMENT_TYPE_ENUM.GENDER,
                        then: yup.object({
                            value: yup
                                .string()
                                .nullable()
                                .test({
                                    message:
                                        ERROR_MESSAGES[COMPLETE_SET_UP_ERRORS.REQUIREMENTS][
                                            REQUIREMENT_TYPE_ENUM.GENDER
                                        ][ERROR_CODES.INPUT_REQUIRED],
                                    test: function (value: string | null) {
                                        if (isCampaignDraftOrForEdit) {
                                            return true
                                        } else {
                                            return Boolean(value)
                                        }
                                    },
                                }),
                        }),
                    })
                    .when('type', {
                        is: REQUIREMENT_TYPE_ENUM.FOLLOWER,
                        then: yup.object({
                            followers: yup
                                .object({
                                    platform: yup
                                        .mixed<PLATFORM_ENUM>()
                                        .oneOf([
                                            PLATFORM_ENUM.IG,
                                            PLATFORM_ENUM.FB,
                                            PLATFORM_ENUM.TIKTOK,
                                            PLATFORM_ENUM.TWITTER,
                                            PLATFORM_ENUM.XIAOHONGSHU,
                                        ]),
                                    min: yup.number().nullable(),
                                    max: yup.number().nullable(),
                                })
                                .test({
                                    message: value => {
                                        return ERROR_MESSAGES[COMPLETE_SET_UP_ERRORS.REQUIREMENTS][
                                            REQUIREMENT_TYPE_ENUM.FOLLOWER
                                        ][value.value.platform][ERROR_CODES.INPUT_REQUIRED]
                                    },
                                    test: function (
                                        this,
                                        value: { platform: number; min: number | null; max: number | null } | null,
                                    ) {
                                        if (isCampaignDraftOrForEdit) {
                                            return true
                                        } else {
                                            if (!value || (value && !value['min'] && !value['max'])) {
                                                return false
                                            } else {
                                                return true
                                            }
                                        }
                                    },
                                })
                                .test({
                                    message: () => i18n.t('errorMessage.validation.maxFollowerLessThanMinFollower'),
                                    test: (value: { min: number | null; max: number | null }) =>
                                        value.max && value.min ? value.max > value.min : true,
                                }),
                        }),
                    })
                    .when('type', {
                        is: REQUIREMENT_TYPE_ENUM.OCCUPATION,
                        then: yup.object({
                            occupations: yup
                                .array()
                                .nullable()
                                .test({
                                    message:
                                        ERROR_MESSAGES[COMPLETE_SET_UP_ERRORS.REQUIREMENTS][
                                            REQUIREMENT_TYPE_ENUM.OCCUPATION
                                        ][ERROR_CODES.INPUT_REQUIRED],
                                    test: function (value: number[] | null) {
                                        if (isCampaignDraftOrForEdit) {
                                            return true
                                        } else {
                                            if (Array.isArray(value)) {
                                                return Boolean(value.length)
                                            } else {
                                                return Boolean(value)
                                            }
                                        }
                                    },
                                })
                                .max(2, i18n.t('errorMessage.validation.selectNoMoreThanTwoChoices')),
                        }),
                    })
                    .when('type', {
                        is: REQUIREMENT_TYPE_ENUM.PROFILE_TYPE,
                        then: yup.object({
                            profile_types: yup
                                .array()
                                .nullable()
                                .test({
                                    message:
                                        ERROR_MESSAGES[COMPLETE_SET_UP_ERRORS.REQUIREMENTS][
                                            REQUIREMENT_TYPE_ENUM.PROFILE_TYPE
                                        ][ERROR_CODES.INPUT_REQUIRED],
                                    test: function (value: number[] | null) {
                                        if (isCampaignDraftOrForEdit) {
                                            return true
                                        } else {
                                            if (Array.isArray(value)) {
                                                return Boolean(value.length)
                                            } else {
                                                return Boolean(value)
                                            }
                                        }
                                    },
                                }),
                        }),
                    })
                    .when('type', {
                        is: REQUIREMENT_TYPE_ENUM.LOCATION,
                        then: yup.object({
                            locations: yup
                                .array()
                                .of(
                                    yup.lazy((value: any) => {
                                        if (value.hasOwnProperty('region')) {
                                            return getLocationSchema('region')
                                        } else if (value.hasOwnProperty('city') && value.hasOwnProperty('district')) {
                                            return getLocationSchema('city')
                                        } else if (value.hasOwnProperty('province') && value.hasOwnProperty('city')) {
                                            return getLocationSchema('province')
                                        } else if (value.hasOwnProperty('state') && value.hasOwnProperty('district')) {
                                            return getLocationSchema('state')
                                        } else {
                                            return yup.object()
                                        }
                                    }),
                                )
                                .test({
                                    message:
                                        ERROR_MESSAGES[COMPLETE_SET_UP_ERRORS.REQUIREMENTS][
                                            REQUIREMENT_TYPE_ENUM.LOCATION
                                        ][ERROR_CODES.INPUT_REQUIRED],
                                    test: function (value: Record<string, string | null>[] | null) {
                                        if (isCampaignDraftOrForEdit) {
                                            return true
                                        } else {
                                            return locationsRequirementValidation(value)
                                        }
                                    },
                                }),
                        }),
                    })
                    .when('type', {
                        is: BLOCKED_CAMPAIGNS_TYPE,
                        then: yup.object({
                            inputValue: yup
                                .string()
                                .nullable()
                                .matches(/^[a-zA-Z0-9]+$/, i18n.t('errorMessage.validation.enterExistingCampaignId')),
                        }),
                    }),
            }),
        ),
    })
}
